import { Text, VStack } from "@chakra-ui/react";
import { formatDate } from "@syla/shared/helpers/formatting";
import { SubscriptionWithPlansAndAccess } from "@syla/shared/types/responses/GetSubscriptionsResponse";
import { getSubscriptionState } from "@syla/shared/types/subscriptions/getSubscriptionState";
import {
  Lifecycle,
  BillingStatus,
  RenewalAction,
} from "@syla/shared/types/subscriptions/subscriptionState";
import React from "react";
import { Plan } from "../../../types/plans/plan";
import { PlanAlert } from "./PlanAlert";

interface SubscriptionAlertProps {
  canManageExistingSubscription: boolean;
  subscription?: SubscriptionWithPlansAndAccess;
  downgradePlan?: Plan;
  isRedirecting: boolean;
  onManageBillingAction: () => Promise<void>;
}

export const SubscriptionAlert = ({
  canManageExistingSubscription,
  subscription,
  downgradePlan,
  isRedirecting,
  onManageBillingAction,
}: SubscriptionAlertProps) => {
  if (!subscription) {
    return null;
  }

  const state = getSubscriptionState(subscription);

  const periodEndDate = formatDate(state.periodEnd);

  if (state.lifecycle === Lifecycle.Terminated) {
    return (
      <PlanAlert
        title="Your plan has expired"
        description={
          <>
            Your previous plan is no longer available for renewal.
            <br />
            Please purchase a new plan to continue using Syla.
          </>
        }
      />
    );
  }

  if (canManageExistingSubscription && state.billing === BillingStatus.Unpaid) {
    return (
      <PlanAlert
        title="There's an issue with the billing on your account"
        description={
          <>
            Please update your billing details to retain access to your account.
          </>
        }
        action={{
          label: "Update billing",
          onClickAction: onManageBillingAction,
          isDisabled: isRedirecting,
        }}
      />
    );
  }

  if (canManageExistingSubscription && state.renewal === RenewalAction.Cancel) {
    return (
      <PlanAlert
        title="Your plan has been cancelled"
        description={
          <VStack align="start" spacing={2}>
            <Text>
              Your subscription will expire on <b>{periodEndDate}</b>.
              <br />
              Resume your plan to retain access to your account and avoid losing
              your current pricing and features.
            </Text>

            {!subscription?.plan.active && (
              <Text>
                <Text
                  color="error.500"
                  fontWeight="bold"
                  as="span"
                  display="inline"
                >
                  Important:
                </Text>{" "}
                Your legacy plan cannot be resumed once it expires. <br /> If
                your plan lapses, you’ll need to rejoin on a new plan, at the
                current pricing.
              </Text>
            )}
          </VStack>
        }
      />
    );
  }

  if (state.renewal === RenewalAction.Downgrade && downgradePlan) {
    return (
      <PlanAlert
        title="Your plan will downgrade"
        description={
          <>
            Your subscription will downgrade to the <b>{downgradePlan.name}</b>{" "}
            plan on <b>{periodEndDate}</b>.
            <br />
            Contact support if you would like the downgrade to take effect
            immediately.
          </>
        }
      />
    );
  }

  return null;
};
